import { ROLE_MASTER } from "redux/constants/Auth";
import { useSelector } from "react-redux";

/**
 * Custom hook for permission-based authentication
 * @param {Array} requiredPermissions - Array of permissions required (user needs at least one)
 * @returns {Object} - Authentication state and helper functions
 */
const useAuth = (requiredPermissions = []) => {
  // UserRole is now an array of permissions/roles the user has
  const userPermissions = useSelector((state) => state.auth.role);

  // Ensure userPermissions is always an array
  const permissionsArray = Array.isArray(userPermissions) ? userPermissions : [userPermissions];

  // Check if user is ROLE_MASTER (has all permissions)
  const isMaster = permissionsArray.includes(ROLE_MASTER);

  /**
   * Check if user has any of the required permissions
   * ROLE_MASTER always has access to everything
   */
  const hasPermission = (permissions) => {
    if (isMaster) return true;
    if (!permissions || permissions.length === 0) return true;

    // Check if user has at least one of the required permissions
    return permissions.some(permission => permissionsArray.includes(permission));
  };

  /**
   * Check if user has ALL of the required permissions
   */
  const hasAllPermissions = (permissions) => {
    if (isMaster) return true;
    if (!permissions || permissions.length === 0) return true;

    return permissions.every(permission => permissionsArray.includes(permission));
  };

  /**
   * Check if user has a specific single permission
   */
  const hasExactPermission = (permission) => {
    if (isMaster) return true;
    return permissionsArray.includes(permission);
  };

  /**
   * country list for user access permissions
   */
  const countryListForUser = () => {
    if (isMaster) return true;
    return userPermissions.filter(permission => permission.includes('ROLE_COUNTRY_')).map(permission => permission.split('ROLE_COUNTRY_')[1]);
  };

  // Calculate authentication based on required permissions
  const isAuth = hasPermission(requiredPermissions);

  return {
    userRole: permissionsArray,
    userPermissions: permissionsArray,
    isAuth,
    isMaster,
    hasPermission,
    hasAllPermissions,
    hasExactPermission,
    countryListForUser,
    // Legacy support
    exact: requiredPermissions.length === 1 && permissionsArray.includes(requiredPermissions[0]),
    adminstration: isMaster,
  };
};

export default useAuth;
